
import React from 'react';
import { cn } from '@/lib/utils';

export type ProgressState = 'not_started' | 'in_progress' | 'completed';

interface CircularProgressProps {
  state: ProgressState;
  onStateChange: (newState: ProgressState) => void;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

/**
 * CircularProgress Component
 * 
 * A circular progress indicator that shows three states:
 * - not_started: Empty circle outline
 * - in_progress: Half-filled circle 
 * - completed: Fully filled circle
 * 
 * Features:
 * - Click to cycle through states
 * - Smooth CSS transitions
 * - Configurable size
 * - Accessible with keyboard support
 */
export const CircularProgress: React.FC<CircularProgressProps> = ({
  state,
  onStateChange,
  size = 'md',
  disabled = false,
  className
}) => {
  // Define size variants
  const sizeConfig = {
    sm: { width: 20, height: 20, strokeWidth: 2 },
    md: { width: 24, height: 24, strokeWidth: 2.5 },
    lg: { width: 32, height: 32, strokeWidth: 3 }
  };

  const { width, height, strokeWidth } = sizeConfig[size];
  const radius = (width - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate progress percentage for stroke animation
  const getProgressOffset = (progressState: ProgressState): number => {
    switch (progressState) {
      case 'not_started':
        return circumference; // 0% filled (fully offset)
      case 'in_progress':
        return circumference * 0.5; // 50% filled
      case 'completed':
        return 0; // 100% filled (no offset)
      default:
        return circumference;
    }
  };

  // Cycle through states on click
  const handleClick = () => {
    if (disabled) return;

    const stateOrder: ProgressState[] = ['not_started', 'in_progress', 'completed'];
    const currentIndex = stateOrder.indexOf(state);
    const nextIndex = (currentIndex + 1) % stateOrder.length;
    onStateChange(stateOrder[nextIndex]);
  };

  // Handle keyboard interaction
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  // Get color based on state
  const getStateColor = (progressState: ProgressState): string => {
    switch (progressState) {
      case 'not_started':
        return '#e5e7eb'; // gray-200
      case 'in_progress':
        return '#3b82f6'; // blue-500
      case 'completed':
        return '#10b981'; // emerald-500
      default:
        return '#e5e7eb';
    }
  };

  const stateColor = getStateColor(state);
  const progressOffset = getProgressOffset(state);

  return (
    <div
      className={cn(
        'inline-flex items-center justify-center cursor-pointer transition-transform duration-200',
        !disabled && 'hover:scale-110',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role="button"
      aria-label={`Task progress: ${state.replace('_', ' ')}. Click to change.`}
      title={`Progress: ${state.replace('_', ' ')}`}
    >
      <svg
        width={width}
        height={height}
        className="transform -rotate-90 transition-all duration-300"
      >
        {/* Background circle */}
        <circle
          cx={width / 2}
          cy={height / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
        />
        
        {/* Progress circle */}
        <circle
          cx={width / 2}
          cy={height / 2}
          r={radius}
          fill="none"
          stroke={stateColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={progressOffset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-in-out"
        />
        
        {/* Center dot for completed state */}
        {state === 'completed' && (
          <circle
            cx={width / 2}
            cy={height / 2}
            r={radius * 0.3}
            fill={stateColor}
            className="animate-scale-in"
          />
        )}
      </svg>
    </div>
  );
};
